import {
  CHAIN_DEFINITIONS,
  getFingerRoleNamesForChain,
} from '../../lib/schema/mapping';
import { getChainCssColor } from '../../presentations/chain';
import {
  ADDITIONAL_CHAIN_DIAGRAM_TEMPLATES,
  ADDITIONAL_CHAIN_FAMILY_DEFINITIONS,
  ADDITIONAL_CHAIN_SECTION_DEFINITIONS,
  type AdditionalChainFamilyDefinition,
  type AdditionalChainFamilyMemberDefinition,
  type AdditionalChainSubsectionDefinition,
  formatAdditionalChainName,
  getAdditionalChainInstanceIndex,
  getAdditionalChainMemberChainName,
} from './additionalChainFamilies';
import type {
  BoneAssignmentChainState,
  BoneAssignmentDiagram,
  BoneAssignmentDiagramChainNodeMarker,
  BoneAssignmentDiagramChainOverlaySegment,
} from './types';

export interface AdditionalChainInstanceMember {
  definition: AdditionalChainFamilyMemberDefinition;
  chainName: string;
  chainState: BoneAssignmentChainState;
  isPresent: boolean;
}

export interface AdditionalChainInstance {
  family: AdditionalChainFamilyDefinition;
  instanceIndex: number;
  displayLabel: string;
  diagram: BoneAssignmentDiagram;
  roleDiagram: BoneAssignmentDiagram;
  roleNames: readonly string[];
  removableChainNames: readonly string[];
  members: readonly AdditionalChainInstanceMember[];
}

export interface AdditionalChainFamilyGroup {
  family: AdditionalChainFamilyDefinition;
  instances: readonly AdditionalChainInstance[];
  nextChainName: string | null;
}

export interface AdditionalChainSection {
  key: string;
  label: string;
  defaultOpen: boolean;
  families: readonly AdditionalChainFamilyGroup[];
  subsections: readonly AdditionalChainSubsection[];
}

export interface AdditionalChainSubsection {
  key: string;
  label: string;
  defaultOpen: boolean;
  families: readonly AdditionalChainFamilyGroup[];
}

const EMPTY_CHAIN_ASSIGNMENT: BoneAssignmentChainState = {
  boneRefs: [],
  startBoneRef: null,
  startBoneName: null,
  endBoneRef: null,
  endBoneName: null,
};

export function deriveAdditionalChainSections({
  presentChainNames,
  draftChainNames,
  chainAssignments,
}: {
  presentChainNames: ReadonlySet<string>;
  draftChainNames: ReadonlySet<string>;
  chainAssignments: ReadonlyMap<string, BoneAssignmentChainState>;
}): AdditionalChainSection[] {
  const effectiveChainNames = new Set<string>([
    ...presentChainNames,
    ...draftChainNames,
  ]);

  return ADDITIONAL_CHAIN_SECTION_DEFINITIONS.map((section) => {
    const allFamilies = ADDITIONAL_CHAIN_FAMILY_DEFINITIONS.filter(
      (family) => family.sectionKey === section.key,
    )
      .map((family) =>
        deriveAdditionalChainFamilyGroup({
          family,
          presentChainNames,
          draftChainNames,
          effectiveChainNames,
          chainAssignments,
        }),
      )
      .filter(
        (family) =>
          family.instances.length > 0 || family.nextChainName !== null,
      );
    const families = allFamilies.filter(
      (family) => !family.family.subsectionKey,
    );
    const subsections = (section.subsectionDefinitions ?? [])
      .map((subsectionDefinition) =>
        buildAdditionalChainSubsection(subsectionDefinition, allFamilies),
      )
      .filter((subsection) => subsection.families.length > 0);
    const defaultOpen =
      families.some((family) => family.instances.length > 0) ||
      subsections.some((subsection) => subsection.defaultOpen);

    return {
      key: section.key,
      label: section.label,
      defaultOpen,
      families,
      subsections,
    };
  }).filter(
    (section) => section.families.length > 0 || section.subsections.length > 0,
  );
}

export function buildAdditionalChainInstanceOverlay(
  instance: AdditionalChainInstance,
  {
    focusedChainName,
    pendingChainName,
  }: {
    focusedChainName: string | null;
    pendingChainName: string | null;
  },
): {
  segments: BoneAssignmentDiagramChainOverlaySegment[];
  nodeMarkers: BoneAssignmentDiagramChainNodeMarker[];
} {
  const segments: BoneAssignmentDiagramChainOverlaySegment[] = [];
  const nodeMarkers: BoneAssignmentDiagramChainNodeMarker[] = [];

  for (const member of instance.members) {
    const rolePath = member.definition.templateRolePath;
    if (rolePath.length === 0) continue;

    const startRole = rolePath[0];
    const endRole = rolePath[rolePath.length - 1];
    const overlayBase = {
      startRole,
      endRole,
      startBoneName: member.chainState.startBoneName,
      endBoneName: member.chainState.endBoneName,
      color: getChainCssColor(member.chainName),
      isAssigned: member.chainState.boneRefs.length > 0,
      isFocused: focusedChainName === member.chainName,
      isPending: pendingChainName === member.chainName,
    };

    if (rolePath.length === 1) {
      nodeMarkers.push({
        chainName: member.chainName,
        roleName: startRole,
        ...overlayBase,
      });
      continue;
    }

    for (let index = 1; index < rolePath.length; index += 1) {
      segments.push({
        chainName: member.chainName,
        from: rolePath[index - 1],
        to: rolePath[index],
        ...overlayBase,
      });
    }
  }

  return { segments, nodeMarkers };
}

function deriveAdditionalChainFamilyGroup({
  family,
  presentChainNames,
  draftChainNames,
  effectiveChainNames,
  chainAssignments,
}: {
  family: AdditionalChainFamilyDefinition;
  presentChainNames: ReadonlySet<string>;
  draftChainNames: ReadonlySet<string>;
  effectiveChainNames: ReadonlySet<string>;
  chainAssignments: ReadonlyMap<string, BoneAssignmentChainState>;
}): AdditionalChainFamilyGroup {
  const instanceIndices = collectAdditionalChainFamilyInstanceIndices(
    family,
    effectiveChainNames,
  );

  const instances = Array.from(instanceIndices)
    .sort((left, right) => left - right)
    .map((instanceIndex) =>
      buildAdditionalChainInstance({
        family,
        instanceIndex,
        presentChainNames,
        draftChainNames,
        chainAssignments,
      }),
    );

  return {
    family,
    instances,
    nextChainName: getNextAdditionalChainName(family, effectiveChainNames),
  };
}

function buildAdditionalChainSubsection(
  subsectionDefinition: AdditionalChainSubsectionDefinition,
  sectionFamilies: readonly AdditionalChainFamilyGroup[],
): AdditionalChainSubsection {
  const families = sectionFamilies.filter(
    (family) => family.family.subsectionKey === subsectionDefinition.key,
  );

  return {
    key: subsectionDefinition.key,
    label: subsectionDefinition.label,
    defaultOpen: families.some((family) => family.instances.length > 0),
    families,
  };
}

function collectAdditionalChainFamilyInstanceIndices(
  family: AdditionalChainFamilyDefinition,
  effectiveChainNames: ReadonlySet<string>,
): Set<number> {
  const instanceIndices = new Set<number>();
  const sourceBaseChainNames = new Set<string>(
    family.instanceSourceBaseChainName
      ? [family.instanceSourceBaseChainName]
      : [],
  );

  for (const member of family.members) {
    sourceBaseChainNames.add(member.baseChainName);
  }

  for (const baseChainName of sourceBaseChainNames) {
    for (const chainName of effectiveChainNames) {
      const instanceIndex = getAdditionalChainInstanceIndex(
        baseChainName,
        chainName,
      );
      if (instanceIndex === null) continue;
      if (
        !family.includeBaseInstanceInAdditionalSection &&
        instanceIndex === 1
      ) {
        continue;
      }
      instanceIndices.add(instanceIndex);
    }
  }

  return instanceIndices;
}

function buildAdditionalChainInstance({
  family,
  instanceIndex,
  presentChainNames,
  draftChainNames,
  chainAssignments,
}: {
  family: AdditionalChainFamilyDefinition;
  instanceIndex: number;
  presentChainNames: ReadonlySet<string>;
  draftChainNames: ReadonlySet<string>;
  chainAssignments: ReadonlyMap<string, BoneAssignmentChainState>;
}): AdditionalChainInstance {
  const sourceChainName = family.instanceSourceBaseChainName
    ? formatAdditionalChainName(
        family.instanceSourceBaseChainName,
        instanceIndex,
      )
    : null;
  const members = family.members.map((member) => {
    const chainName = getAdditionalChainMemberChainName(member, instanceIndex);
    const isPresent =
      presentChainNames.has(chainName) || draftChainNames.has(chainName);

    return {
      definition: member,
      chainName,
      chainState: chainAssignments.get(chainName) ?? EMPTY_CHAIN_ASSIGNMENT,
      isPresent,
    };
  });
  const displayLabel =
    instanceIndex === 1 ? family.label : `${family.label} ${instanceIndex}`;
  const removableChainNames = Array.from(
    new Set([
      ...members
        .filter((member) => member.isPresent)
        .map((member) => member.chainName),
      ...((sourceChainName &&
      (presentChainNames.has(sourceChainName) ||
        draftChainNames.has(sourceChainName))
        ? [sourceChainName]
        : []) as string[]),
    ]),
  );

  return {
    family,
    instanceIndex,
    displayLabel,
    diagram: buildAdditionalChainInstanceDiagram({
      family,
      instanceIndex,
      displayLabel,
    }),
    roleDiagram: buildAdditionalChainInstanceRoleDiagram({
      family,
      instanceIndex,
      displayLabel,
    }),
    roleNames: getAdditionalChainInstanceRoleNames(family, instanceIndex),
    removableChainNames,
    members,
  };
}

function buildAdditionalChainInstanceDiagram({
  family,
  instanceIndex,
  displayLabel,
}: {
  family: AdditionalChainFamilyDefinition;
  instanceIndex: number;
  displayLabel: string;
}): BoneAssignmentDiagram {
  const template = ADDITIONAL_CHAIN_DIAGRAM_TEMPLATES[family.templateKey];

  return {
    key: `additional-${family.key}-${instanceIndex}`,
    label: displayLabel,
    minHeightPx: template.minHeightPx,
    maxWidthPx: template.maxWidthPx,
    nodes: template.nodes,
    connections: template.connections,
  };
}

function buildAdditionalChainInstanceRoleDiagram({
  family,
  instanceIndex,
  displayLabel,
}: {
  family: AdditionalChainFamilyDefinition;
  instanceIndex: number;
  displayLabel: string;
}): BoneAssignmentDiagram {
  const template = ADDITIONAL_CHAIN_DIAGRAM_TEMPLATES[family.templateKey];
  const roleNameByTemplateRole = buildAdditionalRoleNameByTemplateRole(
    family,
    instanceIndex,
  );
  const includedTemplateRoleNames = new Set(roleNameByTemplateRole.keys());
  const nodes = template.nodes.flatMap((node) => {
    const roleName = roleNameByTemplateRole.get(node.roleName);
    if (!roleName) return [];

    return [{ ...node, roleName }];
  });

  return {
    key: `additional-role-${family.key}-${instanceIndex}`,
    label: displayLabel,
    minHeightPx: template.minHeightPx,
    maxWidthPx: template.maxWidthPx,
    nodes,
    connections: buildVisibleRoleConnections(
      template.connections,
      includedTemplateRoleNames,
      roleNameByTemplateRole,
    ),
  };
}

function getAdditionalChainInstanceRoleNames(
  family: AdditionalChainFamilyDefinition,
  instanceIndex: number,
): readonly string[] {
  return Array.from(
    new Set(
      buildAdditionalRoleNameByTemplateRole(family, instanceIndex).values(),
    ),
  );
}

function buildAdditionalRoleNameByTemplateRole(
  family: AdditionalChainFamilyDefinition,
  instanceIndex: number,
): Map<string, string> {
  const roleNameByTemplateRole = new Map<string, string>();
  const additionalHandRoleName = getAdditionalHandRoleName(
    family,
    instanceIndex,
  );

  if (additionalHandRoleName) {
    roleNameByTemplateRole.set('hand', additionalHandRoleName);
  }

  for (const member of family.members) {
    for (const [templateRoleName, roleName] of resolveAdditionalMemberRolePairs(
      family,
      member,
      instanceIndex,
    )) {
      roleNameByTemplateRole.set(templateRoleName, roleName);
    }
  }

  return roleNameByTemplateRole;
}

function resolveAdditionalMemberRolePairs(
  family: AdditionalChainFamilyDefinition,
  member: AdditionalChainFamilyMemberDefinition,
  instanceIndex: number,
): readonly (readonly [string, string])[] {
  const instanceSuffix = getAdditionalRoleInstanceSuffix(instanceIndex);

  if (family.key === 'leftArm' || family.key === 'rightArm') {
    const side = family.key === 'leftArm' ? 'Left' : 'Right';
    const rolePairs: (readonly [string, string])[] = [];

    for (const templateRoleName of member.templateRolePath) {
      switch (templateRoleName) {
        case 'clavicle':
          rolePairs.push([
            templateRoleName,
            `${side}Clavicle${instanceSuffix}`,
          ]);
          break;
        case 'upper':
          rolePairs.push([
            templateRoleName,
            `${side}UpperArm${instanceSuffix}`,
          ]);
          break;
        case 'lower':
          rolePairs.push([
            templateRoleName,
            `${side}LowerArm${instanceSuffix}`,
          ]);
          break;
        case 'hand':
          rolePairs.push([templateRoleName, `${side}Hand${instanceSuffix}`]);
          break;
        default:
          break;
      }
    }

    return rolePairs;
  }

  if (family.key === 'leftHandFingers' || family.key === 'rightHandFingers') {
    const chainName = getAdditionalChainMemberChainName(member, instanceIndex);
    const roleNames = getFingerRoleNamesForChain(
      chainName,
      member.templateRolePath.length,
    );

    return member.templateRolePath.flatMap((templateRoleName, index) => {
      const roleName = roleNames[index];
      return roleName ? [[templateRoleName, roleName] as const] : [];
    });
  }

  const chainDefinition = CHAIN_DEFINITIONS[member.baseChainName];
  if (!chainDefinition || member.templateRolePath.length === 0) {
    return [];
  }

  const firstTemplateRoleName = member.templateRolePath[0];
  const lastTemplateRoleName =
    member.templateRolePath[member.templateRolePath.length - 1];
  const rolePairs: (readonly [string, string])[] = [];

  if (chainDefinition.startRole) {
    rolePairs.push([
      firstTemplateRoleName,
      `${chainDefinition.startRole}${instanceSuffix}`,
    ]);
  }

  if (
    chainDefinition.endRole &&
    chainDefinition.endRole !== chainDefinition.startRole &&
    lastTemplateRoleName !== firstTemplateRoleName
  ) {
    rolePairs.push([
      lastTemplateRoleName,
      `${chainDefinition.endRole}${instanceSuffix}`,
    ]);
  }

  return rolePairs;
}

function getAdditionalRoleInstanceSuffix(instanceIndex: number): string {
  return instanceIndex <= 1 ? '' : `_${instanceIndex}`;
}

function getAdditionalHandRoleName(
  family: AdditionalChainFamilyDefinition,
  instanceIndex: number,
): string | null {
  const instanceSuffix = getAdditionalRoleInstanceSuffix(instanceIndex);

  if (family.key === 'leftHandFingers') {
    return `LeftHand${instanceSuffix}`;
  }

  if (family.key === 'rightHandFingers') {
    return `RightHand${instanceSuffix}`;
  }

  return null;
}

function buildVisibleRoleConnections(
  templateConnections: BoneAssignmentDiagram['connections'],
  visibleTemplateRoleNames: ReadonlySet<string>,
  roleNameByTemplateRole: ReadonlyMap<string, string>,
): BoneAssignmentDiagram['connections'] {
  const neighborsByTemplateRole = new Map<string, Set<string>>();

  for (const { from, to } of templateConnections) {
    if (!neighborsByTemplateRole.has(from)) {
      neighborsByTemplateRole.set(from, new Set<string>());
    }
    if (!neighborsByTemplateRole.has(to)) {
      neighborsByTemplateRole.set(to, new Set<string>());
    }
    neighborsByTemplateRole.get(from)?.add(to);
    neighborsByTemplateRole.get(to)?.add(from);
  }

  const visibleConnections = new Map<string, { from: string; to: string }>();

  for (const startTemplateRoleName of visibleTemplateRoleNames) {
    const queue = [
      { current: startTemplateRoleName, previous: null as string | null },
    ];
    const visited = new Set<string>([startTemplateRoleName]);

    while (queue.length > 0) {
      const nextEntry = queue.shift();
      if (!nextEntry) continue;

      for (const neighbor of neighborsByTemplateRole.get(nextEntry.current) ??
        []) {
        if (neighbor === nextEntry.previous || visited.has(neighbor)) continue;
        visited.add(neighbor);

        if (visibleTemplateRoleNames.has(neighbor)) {
          const from = roleNameByTemplateRole.get(startTemplateRoleName);
          const to = roleNameByTemplateRole.get(neighbor);
          if (!from || !to || from === to) continue;

          const connectionKey = [from, to].sort().join('::');
          if (!visibleConnections.has(connectionKey)) {
            visibleConnections.set(connectionKey, { from, to });
          }
          continue;
        }

        queue.push({ current: neighbor, previous: nextEntry.current });
      }
    }
  }

  return Array.from(visibleConnections.values());
}

function getNextAdditionalChainName(
  family: AdditionalChainFamilyDefinition,
  existingChainNames: ReadonlySet<string>,
): string | null {
  if (family.instanceSourceBaseChainName) {
    const startIndex = family.includeBaseInstanceInAdditionalSection ? 1 : 2;
    for (
      let instanceIndex = startIndex;
      family.maxInstanceIndex === null ||
      instanceIndex <= family.maxInstanceIndex;
      instanceIndex += 1
    ) {
      const chainName = formatAdditionalChainName(
        family.instanceSourceBaseChainName,
        instanceIndex,
      );
      if (!existingChainNames.has(chainName)) {
        return chainName;
      }
    }

    return null;
  }

  if (!family.addMemberKey) return null;

  const addMember = family.members.find(
    (member) => member.key === family.addMemberKey,
  );
  if (!addMember) return null;

  const startIndex = family.includeBaseInstanceInAdditionalSection ? 1 : 2;
  for (
    let instanceIndex = startIndex;
    family.maxInstanceIndex === null ||
    instanceIndex <= family.maxInstanceIndex;
    instanceIndex += 1
  ) {
    const chainName = getAdditionalChainMemberChainName(
      addMember,
      instanceIndex,
    );
    if (!existingChainNames.has(chainName)) {
      return chainName;
    }
  }

  return null;
}
